import { Plus } from "@tamagui/lucide-icons";
import { useRef, useState } from "react";
import { ScrollView, XStack, YStack } from "tamagui";

import {
  useGetMedicationHistoryStatus,
  useSetActiveMedications,
} from "@/api/generated";
import { FetchRxHistoryWorkflowStatusStatus } from "@/api/generated/models";
import {
  Button,
  Checkbox,
  Footer,
  Heading,
  LoadingSpinner,
  Paragraph,
  Text,
} from "@/components";
import {
  AddMedicationDialog,
  type ManualMedication,
} from "./AddMedicationDialog";

type ManualMedRow = ManualMedication & { tempId: number };

export const ActiveMedications = ({
  onContinue,
}: {
  onContinue: () => void;
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [manualMeds, setManualMeds] = useState<ManualMedRow[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const nextTempIdRef = useRef(-1);

  const { data: rxHistoryStatus, isError: statusFetchFailed } =
    useGetMedicationHistoryStatus({
      query: {
        refetchInterval: (query) =>
          query.state.data?.status ===
          FetchRxHistoryWorkflowStatusStatus.in_progress
            ? 5000
            : false,
        meta: { suppressErrorToast: true },
      },
    });

  const { mutate: setActiveMedications, isPending } = useSetActiveMedications({
    mutation: {
      onSuccess: onContinue,
      onError: onContinue,
    },
  });

  const status = rxHistoryStatus?.status;
  const allMedications = [
    ...(rxHistoryStatus?.medications ?? []),
    ...(rxHistoryStatus?.non_renewable_medications ?? []),
  ];

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAddManual = (med: ManualMedication) => {
    const tempId = nextTempIdRef.current;
    nextTempIdRef.current -= 1;
    setManualMeds((prev) => [...prev, { ...med, tempId }]);
    setSelectedIds((prev) => new Set(prev).add(tempId));
  };

  const handleContinue = () => {
    const positiveIds: number[] = [];
    const selectedManual: ManualMedRow[] = [];
    for (const id of selectedIds) {
      if (id >= 0) positiveIds.push(id);
      else {
        const match = manualMeds.find((m) => m.tempId === id);
        if (match) selectedManual.push(match);
      }
    }
    setActiveMedications({
      data: {
        medication_history_ids: positiveIds,
        manual_medications: selectedManual.map((m) => ({
          name: m.name,
          strength: m.strength,
          directions: m.directions,
        })),
      },
    });
  };

  const isLoading =
    !statusFetchFailed &&
    (!rxHistoryStatus ||
      status === FetchRxHistoryWorkflowStatusStatus.in_progress);

  if (isLoading) {
    return (
      <YStack
        flex={1}
        justifyContent="center"
        alignItems="center"
        gap="$100"
        paddingHorizontal="$200"
        paddingBottom="$200"
      >
        <LoadingSpinner size="$1000" />
        <Text semibold>Looking for prescriptions</Text>
        <Text color="$textSecondary">This can take up to 3 minutes...</Text>
      </YStack>
    );
  }

  return (
    <YStack flex={1}>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          padding: "$200",
          gap: "$200",
        }}
      >
        <YStack gap="$100">
          <Heading size="$xl">
            Which medications are you currently taking?
          </Heading>
          <Paragraph color="$textSecondary" size="$s">
            Select your current medications below. Please add any medications
            that are not listed.
          </Paragraph>
        </YStack>
        <YStack gap="$100">
          {allMedications.map((med) => {
            const id = med.patient_vendor_medication_history_id;
            const name =
              med.prescription_details.display_name ??
              med.prescription_details.generic_drug_name ??
              "Unknown medication";
            return (
              <Checkbox
                key={id}
                label={name}
                wrapped
                checked={selectedIds.has(id)}
                onCheckedChange={() => toggle(id)}
              />
            );
          })}
          {manualMeds.map((med) => (
            <Checkbox
              key={med.tempId}
              label={`${med.name} ${med.strength}`.trim()}
              wrapped
              checked={selectedIds.has(med.tempId)}
              onCheckedChange={() => toggle(med.tempId)}
            />
          ))}
          <Button.Wrapper
            onPress={() => setShowAddDialog(true)}
            aria-label="Add another medication"
            dataTrackingId="active-medications-add-manual-button"
          >
            <XStack
              gap="$100"
              alignItems="center"
              paddingVertical="$100"
              paddingHorizontal="$100"
            >
              <Plus size="$150" color="$textLink" />
              <Text color="$textLink" semibold>
                Add another medication
              </Text>
            </XStack>
          </Button.Wrapper>
        </YStack>
      </ScrollView>

      <Footer padding="$200" mode="dialog">
        <Button
          mode="primary"
          constrained
          onPress={handleContinue}
          loading={isPending}
          dataTrackingId="active-medications-continue-button"
        >
          Continue
        </Button>
      </Footer>

      <AddMedicationDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={handleAddManual}
      />
    </YStack>
  );
};
